import { useCallback, useEffect, useMemo, useState } from 'react';

import type { DynamicRowHeight } from 'react-window';

export const ROW_ID_ATTRIBUTE = 'data-row-id';

/** Spread onto the element react-window positions, the one that owns the `style` it passes down. */
export const rowIdProps = (rowId: string): Record<typeof ROW_ID_ATTRIBUTE, string> => ({
  [ROW_ID_ATTRIBUTE]: rowId,
});

interface RowIdHeightCacheOptions {
  defaultRowHeight: number;
  /** Must be a new function on every reorder, or rows entering the window are never observed. */
  rowId: (index: number) => string | undefined;
}

/**
 * react-window's `useDynamicRowHeight` keyed by row id instead of slot index.
 *
 * The built-in cache files heights under the index it stamps onto each row. Once rows are keyed by
 * identity React moves their nodes rather than rewriting them, so nothing resizes, no stamp is
 * refreshed, and the cached heights describe the previous order until something forces a
 * re-measure. Rows must carry {@link rowIdProps} or they are never measured.
 */
export const useRowIdHeightCache = ({
  defaultRowHeight,
  rowId,
}: RowIdHeightCacheOptions): DynamicRowHeight => {
  const [heights, setHeights] = useState<ReadonlyMap<string, number>>(() => new Map());

  // Same map for an unchanged height, so react-window does not re-lay out on every message.
  const setHeightForId = useCallback((id: string, height: number) => {
    setHeights((current) => {
      if (current.get(id) === height) {
        return current;
      }
      const next = new Map(current);
      next.set(id, height);
      return next;
    });
  }, []);

  const [observer] = useState(() => {
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    return new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const id = entry.target.getAttribute(ROW_ID_ATTRIBUTE);
        const [borderBox] = entry.borderBoxSize ?? [];
        const height = borderBox ? borderBox.blockSize : entry.contentRect.height;
        // Zero height is a hidden row, not a collapsed one.
        if (id && height) {
          setHeightForId(id, height);
        }
      });
    });
  });

  useEffect(() => () => observer?.disconnect(), [observer]);

  const observeRowElements = useCallback(
    (elements: Element[] | NodeListOf<Element>) => {
      if (!observer) {
        return () => {};
      }
      elements.forEach((element) => observer.observe(element));
      return () => {
        elements.forEach((element) => observer.unobserve(element));
      };
    },
    [observer]
  );

  const getRowHeight = useCallback(
    (index: number) => {
      const id = rowId(index);
      // undefined sends react-window to getAverageRowHeight, a better guess than the default.
      return id === undefined ? undefined : heights.get(id);
    },
    [heights, rowId]
  );

  // Memoized, not just a callback: react-window asks once per unmeasured row, and an unmeasured
  // row stays unmeasured here, where its own cache back-fills the default on read.
  const average = useMemo(() => {
    if (heights.size === 0) {
      return defaultRowHeight;
    }
    let total = 0;
    heights.forEach((height) => {
      total += height;
    });
    return total / heights.size;
  }, [defaultRowHeight, heights]);

  const getAverageRowHeight = useCallback(() => average, [average]);

  const setRowHeight = useCallback(
    (index: number, height: number) => {
      const id = rowId(index);
      if (id !== undefined) {
        setHeightForId(id, height);
      }
    },
    [rowId, setHeightForId]
  );

  // New identity on each reorder re-runs react-window's observe effect for rows that just entered.
  return useMemo(
    () => ({ getAverageRowHeight, getRowHeight, observeRowElements, setRowHeight }),
    [getAverageRowHeight, getRowHeight, observeRowElements, setRowHeight]
  );
};
